import { FunctionComponent, useState } from 'react'
import Dialog from '@/components/_custom/Dialog/Dialog'
import DialogHeader from '@/components/_custom/DialogHeader/DialogHeader'
import { useTranslation } from 'react-i18next'
import DialogBody from '@/components/_custom/DialogBody/DialogBody'
import UserInput from '@/components/_custom/UserInput/userInput'
import styles from './editApiClientInformationDialog.module.scss'
import Checkbox from '@/components/_custom/Checkbox/Checkbox'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import { useMutation } from '@apollo/client'
import { useNotification } from '@/modules/context/NotificationContext.tsx'
import { UPDATE_API_CLIENT } from '@/modules/API/apiClient/apiClientHandler.ts'
import { ApiClient, ApiClientDaycare } from '@/types/ApiClient.ts'
import SelectMultiple from '@/components/_custom/Selector/selectMultiple/selectMultiple.tsx'
import { SelectOptionDeprecated, SelectOptionsDeprecated } from '@/types/Select.ts'
import { Daycare } from '@/types/Daycare.ts'
import { useAuth } from '@/modules/context/AuthContext.tsx'

interface EditApiClientInformationDialogProps {
    open: boolean
    handleClose: () => void
    apiClient: ApiClient
    refetch: () => void
}

const EditApiClientInformationDialog: FunctionComponent<EditApiClientInformationDialogProps> = ({
    open,
    handleClose,
    apiClient,
    refetch
}: EditApiClientInformationDialogProps) => {
    const { t } = useTranslation()

    const { user } = useAuth()

    const { showNotificationSuccess, showNotificationError } = useNotification()

    const [nameEdit, setNameEdit] = useState<string>(apiClient.name)
    const [activeEdit, setActiveEdit] = useState<boolean>(apiClient.active || false)

    const [daycareIds, setDaycareIds] = useState<string[]>(
        apiClient.daycares.map((d: ApiClientDaycare): string => d.id.toString())
    )

    const [updateApiClient] = useMutation(UPDATE_API_CLIENT, {
        variables: {
            id: apiClient.id,
            input: {
                name: nameEdit,
                active: activeEdit,
                daycareIds: daycareIds.map(Number)
            }
        },
        onCompleted: () => {
            showNotificationSuccess(t('apiClientUpdated'))

            refetch()
            handleClose()
        },
        onError: () => {
            showNotificationError(t('errorUpdatedApiClient'))
        }
    })

    const daycareIdsOptions: SelectOptionsDeprecated = [
        {
            options:
                user?.daycares.map(
                    (daycare: Daycare): SelectOptionDeprecated => ({
                        name: daycare.city + ' - ' + daycare.name,
                        value: daycare.id.toString()
                    })
                ) || []
        }
    ]

    const originalDaycareIds: string = apiClient.daycares
        .map((d: ApiClientDaycare): string => d.id.toString())
        .sort((a: string, b: string): number => a.localeCompare(b))
        .join()

    const disabled: boolean =
        nameEdit === apiClient.name &&
        activeEdit === apiClient.active &&
        [...daycareIds].sort((a: string, b: string): number => a.localeCompare(b)).join() === originalDaycareIds

    return (
        <Dialog open={open} handleClose={handleClose}>
            <DialogHeader title={t('editApiClient')} />
            <DialogBody className={styles.DialogBody}>
                <div className={styles.InputContainer}>
                    <UserInput
                        label={t('name')}
                        className={styles.UserInput}
                        value={nameEdit}
                        setValue={setNameEdit}
                        format={'pascal'}
                    />
                    <SelectMultiple
                        selectOptions={daycareIdsOptions}
                        selectValues={daycareIds}
                        setSelectValues={setDaycareIds}
                        className={styles.UserInput}
                        label={t('structures')}
                    />
                </div>
                <div className={styles.CheckBoxContainer}>
                    <Checkbox
                        text={t('active')}
                        checked={activeEdit}
                        onChange={setActiveEdit}
                        className={styles.CheckBox}
                    />
                </div>
                <div className={styles.ButtonContainer}>
                    <ButtonTextIcon type={'close'} onClick={handleClose} className={styles.Button} />
                    <ButtonTextIcon
                        type={'update'}
                        onClick={updateApiClient}
                        className={styles.Button}
                        disabled={disabled}
                    />
                </div>
            </DialogBody>
        </Dialog>
    )
}

export default EditApiClientInformationDialog
